/**
 * 订单支付页：外跳第三方支付网关后返回时的状态恢复
 * （云纵 / 宁波银行 / 工商等 location.replace 间联支付）
 *
 * sessionStorage 标记约定：
 * - 外跳前写入；recover / 过期 sweep / 主动离页 clear 时删除（有效期 5 分钟）
 * - 关闭浏览器标签后 sessionStorage 整体清空，不会跨会话遗留
 */

import { clearPayAuthCode } from './exitOrderPopupAd'
import { logger } from './pyunLogger'

export const PAY_GATEWAY_REDIRECT_KEYS = {
  PAY_MONEY: 'pay_gateway_redirect_pay_money',
  ESCAPE_OUT: 'pay_gateway_redirect_escape_out'
}

export const PAY_REDIRECT_EXPIRE_MS = 5 * 60 * 1000

export function needRefreshPayCode (vm) {
  if (parseFloat(vm.actually_paid) > 0) return true
  const escData = vm.escapeInfo || vm.escData
  if (parseFloat(vm.actually_paid) === 0 && escData && Number(escData.total) > 0) return true
  if (parseFloat(vm.actually_paid) === 0 && vm.discount_coupon_ids_send && vm.discount_coupon_ids_send.length > 0) {
    return true
  }
  return false
}

export function markPayRedirectSession (storageKey, plateNumber) {
  sessionStorage.setItem(storageKey, JSON.stringify({
    plate_number: plateNumber || '',
    timestamp: Date.now()
  }))
}

export function markGatewayPayRedirect (storageKey, plateNumber) {
  markPayRedirectSession(storageKey, plateNumber)
}

export function isPayRedirectMarkerExpired (data, expireMs = PAY_REDIRECT_EXPIRE_MS) {
  return !data || !data.timestamp || Date.now() - data.timestamp > expireMs
}

/** 标记是否属于当前订单（车牌不一致则视为脏数据） */
export function isPayRedirectMarkerForPage (data, vm) {
  if (!data || !data.plate_number) return true
  if (!vm || !vm.plate_number) return false
  return String(data.plate_number) === String(vm.plate_number)
}

export function clearPayRedirectMarker (storageKey) {
  if (!storageKey) return
  sessionStorage.removeItem(storageKey)
}

export function clearPayRedirectMarkers (storageKeys) {
  if (!Array.isArray(storageKeys)) return
  storageKeys.forEach(clearPayRedirectMarker)
}

/** 仅清除过期或损坏的标记，不触发恢复副作用 */
export function sweepPayRedirectMarker (storageKey, expireMs = PAY_REDIRECT_EXPIRE_MS) {
  const raw = sessionStorage.getItem(storageKey)
  if (!raw) return
  try {
    const data = JSON.parse(raw)
    if (isPayRedirectMarkerExpired(data, expireMs)) {
      sessionStorage.removeItem(storageKey)
    }
  } catch (err) {
    sessionStorage.removeItem(storageKey)
  }
}

/**
 * 消费外跳恢复标记：读后即删；过期 / 车牌不符 / 解析失败也只清理不恢复
 */
export function recoverPayRedirectSession (storageKey, vm, options = {}) {
  const expireMs = options.expireMs || PAY_REDIRECT_EXPIRE_MS
  const logLabel = options.logLabel || 'PayPage'
  const raw = sessionStorage.getItem(storageKey)
  if (!raw) return false

  try {
    const data = JSON.parse(raw)
    if (isPayRedirectMarkerExpired(data, expireMs)) {
      sessionStorage.removeItem(storageKey)
      return false
    }
    if (!isPayRedirectMarkerForPage(data, vm)) {
      sessionStorage.removeItem(storageKey)
      logger.info(`[${logLabel}] 外跳标记车牌与当前订单不一致，已清除`, { storageKey })
      return false
    }

    sessionStorage.removeItem(storageKey)
    vm.isPress = false
    clearPayAuthCode(vm)
    if (needRefreshPayCode(vm) && typeof vm.getAllCode === 'function') {
      vm.getAllCode()
    }
    logger.info(`[${logLabel}] 外跳返回，已恢复支付状态`, { storageKey })
    return true
  } catch (err) {
    sessionStorage.removeItem(storageKey)
    logger.error(`[${logLabel}] 外跳标记恢复失败，已清除`, err)
    return false
  }
}

/**
 * 外跳支付网关（云纵 / 宁波银行 / 工商等）
 */
export function redirectToPaymentGateway (vm, storageKey, url) {
  if (!url) return
  markGatewayPayRedirect(storageKey, vm.plate_number)
  vm.isPress = true
  window.location.replace(url)
}

export function recoverFromGatewayPayRedirect (storageKey, vm) {
  return recoverPayRedirectSession(storageKey, vm, { logLabel: 'PayPage' })
}

export function bindGatewayPayRedirectListeners (storageKey, vm) {
  const handler = () => recoverFromGatewayPayRedirect(storageKey, vm)
  const onPageShow = (e) => {
    if (e.persisted || sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('visibilitychange', onVisibilityChange)
  return { onPageShow, onVisibilityChange }
}

export function unbindGatewayPayRedirectListeners (handlers) {
  if (!handlers) return
  window.removeEventListener('pageshow', handlers.onPageShow)
  document.removeEventListener('visibilitychange', handlers.onVisibilityChange)
}

/**
 * 微信/农行 JSAPI 收银台取消或失败后：留在订单页，解锁按钮并刷新 code 以便再次支付
 * （不使用 sessionStorage 标记）
 */
export function resumePayOnOrderPage (vm) {
  if (!vm) return
  vm.isPress = false
  clearPayAuthCode(vm)
  if (needRefreshPayCode(vm) && typeof vm.getAllCode === 'function') {
    vm.getAllCode()
  }
  logger.info('[PayPage] 收银台取消/失败，已恢复可再次支付')
}
